import type { ReadingLayout } from "odyssey-shared";
import type { Interaction } from "../connection.ts";

/**
 * The reading panel's state (P1.9-T37, ADR-061's amendment "the reading panel"), owned by the game rather than by
 * React, as the HUD's store is. A granted reply that carries text opens it with that text, which is every sign, since
 * the map server answers a granted verb with the thing's `text` field and only the sign's schema has one; a second
 * such reply replaces what it shows as a new reading. It closes when asked (Enter, E, Escape or its Close button, through
 * the page's layer), when the thing it shows no longer reaches the predicted character (the scene asks each frame
 * through `keepWhile`) and when the page leaves the world. A refusal, or a granted reply with nothing to say, never
 * touches it, so the scene keeps those for the message line. Listeners are told only on a change, and the snapshot is
 * replaced on each change and kept otherwise, as `useSyncExternalStore` needs.
 *
 * @example
 * ```ts
 * const reading = new ReadingStore();
 * if (!reading.receive(interaction)) {
 *   say(interaction.text);
 * }
 * reading.keepWhile((handle) => thingReaches(lookup, handle, predicted, entities));
 * ```
 */

/**
 * One reading: the thing whose text is shown, the text as the map server sent it, how it reads when it is taller
 * than the panel (ADR-077), and a number that changes with every reading, so a replacement starts from the top of its
 * text or its first page.
 */
export interface Reading {
  readonly handle: number;
  readonly text: string;
  readonly layout: ReadingLayout;
  readonly serial: number;
}

/**
 * What the panel's view reads: what to show, or null while closed, and a way to hear that it changed. Both work
 * unbound, as React calls them.
 */
export interface ReadingSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => Reading | null;
}

/**
 * What the scene tells the panel: each reply, whether it still reaches the thing it shows, and that it should close.
 */
export interface ReadingFeed {
  /**
   * Whether the panel is open.
   */
  readonly open: boolean;
  /**
   * Takes a reply that has something to say: a granted one with text.
   *
   * @param interaction The reply.
   * @returns True when the panel took it, so nothing else should show it.
   */
  receive(interaction: Interaction): boolean;
  /**
   * Closes the panel while the thing it shows no longer reaches the character.
   *
   * @param reaches Whether a thing, by handle, still reaches the character; asked only while the panel is open.
   */
  keepWhile(reaches: (handle: number) => boolean): void;
  /**
   * Closes the panel, if it is open.
   */
  close(): void;
}

/**
 * The store.
 */
export class ReadingStore implements ReadingSource, ReadingFeed {
  private readonly listeners = new Set<() => void>();
  private current: Reading | null = null;
  private serial = 0;

  /**
   * Whether the panel is open.
   *
   * @returns True while it shows a reading.
   */
  public get open(): boolean {
    return this.current !== null;
  }

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the panel shows.
   *
   * @returns The reading, or null while closed.
   */
  public readonly getSnapshot = (): Reading | null => this.current;

  /**
   * Opens the panel with a granted reply's text, replacing any reading it shows.
   *
   * @param interaction The reply.
   * @returns True when the reply was granted and carried text.
   */
  public receive(interaction: Interaction): boolean {
    if (!interaction.ok || interaction.text === "") {
      return false;
    }
    this.serial += 1;
    this.set({ handle: interaction.handle, text: interaction.text, layout: interaction.layout, serial: this.serial });
    return true;
  }

  /**
   * Closes the panel when the thing it shows no longer reaches the character.
   *
   * @param reaches Whether a thing still reaches the character.
   */
  public keepWhile(reaches: (handle: number) => boolean): void {
    const current = this.current;
    if (current !== null && !reaches(current.handle)) {
      this.set(null);
    }
  }

  /**
   * Closes the panel, once.
   */
  public close(): void {
    if (this.current !== null) {
      this.set(null);
    }
  }

  /**
   * Replaces the reading and tells every listener.
   *
   * @param next The reading, or null to close.
   */
  private set(next: Reading | null): void {
    this.current = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
